import { Auth } from "aws-amplify";
import React, {
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState
} from "react";
import { useTranslation } from "react-i18next";
import * as Sentry from "@sentry/react";

import { IPerson, RoleUser } from "../interfaces/intarfaces";
import { useFetchUser } from "utils/hooks/useFetchUser";
import { useLocation, useNavigate, matchPath } from "react-router-dom";
import {
  ERROR_404,
  ERROR_ANY,
  HOST_PROFILE_EDIT_FIRST_ROUTE,
  LOGIN_ROUTE,
  STUDENT_PROFILE_EDIT_FIRST_ROUTE
} from "utils/constants";
import { signOut } from "../utils/utilAmplify";
import {
  publicRoutes,
  publicRoutesHospi,
} from "../routes";

interface IContextHospi {
  currentUserId: string | undefined;
  setCurrentUserId: Dispatch<SetStateAction<string | undefined>>;
  currentUserName: string | undefined;
  currentUserRole: RoleUser | undefined;
  setCurrentUserRole: (user: RoleUser | undefined) => void;
  userLoading: boolean;
  isLoadingCurrentUser: boolean | "";
  userProfile: IPerson | undefined;
  updateProfile: Dispatch<SetStateAction<IPerson | undefined>>;
  logOut: () => void;
}

const ContextHospi = createContext<IContextHospi>({
  currentUserId: undefined,
  userLoading: true,
  isLoadingCurrentUser: true,
  currentUserRole: undefined,
  setCurrentUserId: () => console.log("setCurrentUserId"),
  currentUserName: "unknown",
  userProfile: undefined,
  setCurrentUserRole: () => console.log("setCurrentUserRole"),
  updateProfile: () => console.log("change profile"),
  logOut: () => console.log("logOut")
});

export const useContextHospi = (): IContextHospi => {
  return useContext<IContextHospi>(ContextHospi);
};

export const ContextHospiProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentUserRole, setCurrentUserRole] = useState<RoleUser | undefined>(
    undefined
  );
  const [currentUserID, setCurrentUserId] = useState<string | undefined>(
    undefined
  );
  const [profileData, setProfileData] = useState<IPerson | undefined>(
    undefined
  );
  const [userLoading, setUserLoading] = useState<boolean>(true);

  const { i18n } = useTranslation();

  // GET to SERVER and fetch data Profile user with id = currentUserID
  // Start only when changed state currentUserID
  const {
    data: currentUser,
    error: currentUserError,
    loading: isLoadingCurrentUser
  } = useFetchUser(currentUserID);

  useEffect(() => {
    setUserLoading(true);
    Auth.currentAuthenticatedUser()
      .then(({ attributes }) => {
        setCurrentUserId(attributes.sub);
        // load namespaces async without waiting result
        // preloading namespaces fixes problem with blinking screen caused by Suspense
        i18n.loadNamespaces([
          "hostrooms",
          "student",
          "footer",
          "contact",
          "host",
          "hostVerify",
          "matches",
          "chats"
        ]);
      })
      .catch(() => {

        if (![...publicRoutes, ...publicRoutesHospi].some((route) => matchPath(route.path, location.pathname))) {
          navigate(`/${i18n.language}/`);
        }
      })
      .finally(() => {
        setUserLoading(false);
      });
  }, []);

  useEffect(() => {
    let cleanedUp = false;
    if (!currentUserID) {
      Sentry.setUser(null);
      return;
    }
    Auth.currentSession().then((res) => {
      if (cleanedUp) return;
      Sentry.setUser({
        id: res.getIdToken().payload.sub,
        email: res.getIdToken().payload.email
      });
    });
    return () => {
      cleanedUp = true;
    };
  }, [currentUserID]);

  useEffect(() => {
    if (currentUser) {
      setProfileData(currentUser);
      setCurrentUserRole(currentUser.user_type);
    } else {
      setCurrentUserRole(undefined);
      setProfileData(undefined);
    }
  }, [currentUser]);

  useEffect(() => {
    // If profile is absent
    if (currentUserError === ERROR_404) {
      Auth.currentAuthenticatedUser().then(({ attributes }) => {
        let curUserRole = currentUserRole ?? attributes.profile;
        setCurrentUserRole(curUserRole);
        navigate(
          curUserRole === "student"
            ? `/${i18n.language}${STUDENT_PROFILE_EDIT_FIRST_ROUTE.slice(5)}`
            : `/${i18n.language}${HOST_PROFILE_EDIT_FIRST_ROUTE.slice(5)}`
        );
      });
    } else if (currentUserError === ERROR_ANY) {
      signOut().then(() => {
        if (location.pathname === LOGIN_ROUTE) navigate(`/${i18n.language}/`);
      });
    }
    // eslint-disable-next-line
  }, [currentUserError]);

  const getUserField = <T extends keyof IPerson>(k: T) => {
    return (profileData as IPerson)?.[k] || "Unknown";
  };

  const changeUserRole = async (userRole: RoleUser | undefined) => {
    setCurrentUserRole(userRole);
    try {
      const userPrf = await Auth.currentAuthenticatedUser();
      await Auth.updateUserAttributes(userPrf, {
        profile: userRole
      });
      userRole &&
        navigate(
          userRole === "student"
            ? `/${i18n.language}${STUDENT_PROFILE_EDIT_FIRST_ROUTE.slice(5)}`
            : `/${i18n.language}${HOST_PROFILE_EDIT_FIRST_ROUTE.slice(5)}`
        );
    } catch (error) { }
  };

  const logOut = () => {
    signOut().then(() => {
      setCurrentUserId(undefined);
      setProfileData(undefined);
      navigate(`/${i18n.language}/`);
    });
  };

  return (
    <ContextHospi.Provider
      value={{
        currentUserId: currentUserID,
        userLoading,
        isLoadingCurrentUser: isLoadingCurrentUser,
        currentUserRole,
        setCurrentUserId,
        currentUserName: getUserField("first_name"),
        userProfile: profileData,
        setCurrentUserRole: changeUserRole,
        updateProfile: setProfileData,
        logOut
      }}
    >
      {children}
    </ContextHospi.Provider>
  );
};
